import api from './api';

const activityLogService = {
  // Get all activity logs with filters
  getLogs: async (params = {}) => {
    const response = await api.get('/activity-logs', { params });
    return {
      logs: response.data.logs || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get recent activity logs
  getRecent: async (limit = 20, entityType = null) => {
    const params = { limit };
    if (entityType) params.entity_type = entityType;
    
    const response = await api.get('/activity-logs/recent', { params });
    return response.data.logs || [];
  },

  // Get activity statistics
  getStats: async (params = {}) => {
    const response = await api.get('/activity-logs/stats', { params });
    return response.data;
  },

  // Get activity timeline
  getTimeline: async (params = {}) => {
    const response = await api.get('/activity-logs/timeline', { params });
    return response.data.timeline || {};
  },

  // Get activity logs by entity
  getByEntity: async (entityType, entityId, params = {}) => {
    const response = await api.get(`/activity-logs/entity/${entityType}/${entityId}`, { params });
    return {
      logs: response.data.logs || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get activity logs by user
  getByUser: async (userId, params = {}) => {
    const response = await api.get(`/activity-logs/user/${userId}`, { params });
    return {
      logs: response.data.logs || [],
      pagination: response.data.pagination || {}
    };
  },

  // Get single activity log by ID
  getById: async (id) => {
    const response = await api.get(`/activity-logs/${id}`);
    return response.data.log;
  },

  // Create new activity log
  create: async (logData) => {
    const response = await api.post('/activity-logs', logData);
    return response.data;
  },

  // Create bulk activity logs
  createBulk: async (logs) => {
    const response = await api.post('/activity-logs/bulk', { logs });
    return response.data;
  },

  // Activity action configurations
  actionTypes: {
    created: {
      icon: '➕',
      color: 'green',
      label: 'Created',
      verb: 'created'
    },
    updated: {
      icon: '✏️',
      color: 'blue',
      label: 'Updated',
      verb: 'updated'
    },
    deleted: {
      icon: '🗑️',
      color: 'red',
      label: 'Deleted',
      verb: 'deleted'
    },
    assigned: {
      icon: '👤',
      color: 'purple',
      label: 'Assigned',
      verb: 'assigned'
    },
    unassigned: {
      icon: '👤',
      color: 'gray',
      label: 'Unassigned',
      verb: 'unassigned'
    },
    completed: {
      icon: '✅',
      color: 'green',
      label: 'Completed',
      verb: 'completed'
    },
    reopened: {
      icon: '🔄',
      color: 'orange',
      label: 'Reopened',
      verb: 'reopened'
    },
    approved: {
      icon: '✓',
      color: 'green',
      label: 'Approved',
      verb: 'approved'
    },
    rejected: {
      icon: '✗',
      color: 'red',
      label: 'Rejected',
      verb: 'rejected'
    },
    uploaded: {
      icon: '📤',
      color: 'blue',
      label: 'Uploaded',
      verb: 'uploaded'
    },
    downloaded: {
      icon: '📥',
      color: 'blue',
      label: 'Downloaded',
      verb: 'downloaded'
    },
    commented: {
      icon: '💬',
      color: 'blue',
      label: 'Commented',
      verb: 'commented on'
    },
    mentioned: {
      icon: '@',
      color: 'purple',
      label: 'Mentioned',
      verb: 'mentioned in'
    }
  },

  // Entity type configurations
  entityTypes: {
    Project: { icon: '📁', color: 'blue', label: 'Project' },
    Task: { icon: '📋', color: 'green', label: 'Task' },
    Issue: { icon: '🐛', color: 'red', label: 'Issue' },
    Document: { icon: '📄', color: 'blue', label: 'Document' },
    Budget: { icon: '💰', color: 'yellow', label: 'Budget' },
    Expense: { icon: '💳', color: 'orange', label: 'Expense' },
    Risk: { icon: '⚡', color: 'red', label: 'Risk' },
    Milestone: { icon: '🎯', color: 'purple', label: 'Milestone' },
    TimeEntry: { icon: '⏱️', color: 'blue', label: 'Time Entry' },
    Comment: { icon: '💬', color: 'gray', label: 'Comment' }
  },

  // Helper: Get action config
  getActionConfig: (action) => {
    return activityLogService.actionTypes[action] || {
      icon: '📝',
      color: 'gray',
      label: action,
      verb: action
    };
  },

  // Helper: Get entity type config
  getEntityTypeConfig: (entityType) => {
    return activityLogService.entityTypes[entityType] || {
      icon: '📦',
      color: 'gray',
      label: entityType
    };
  },

  // Helper: Format activity log for display
  formatLog: (log) => {
    const actionConfig = activityLogService.getActionConfig(log.action);
    const entityConfig = activityLogService.getEntityTypeConfig(log.entity_type);
    
    return {
      ...log,
      actionIcon: actionConfig.icon,
      actionColor: actionConfig.color,
      actionLabel: actionConfig.label,
      actionVerb: actionConfig.verb,
      entityIcon: entityConfig.icon,
      entityColor: entityConfig.color,
      entityLabel: entityConfig.label,
      formattedDate: activityLogService.formatDate(log.created_at),
      timeAgo: activityLogService.getTimeAgo(log.created_at),
      userName: log.user ? `${log.user.first_name} ${log.user.last_name}` : 'Unknown User',
      userAvatar: log.user?.avatar_url || null,
      description: log.description || activityLogService.generateDescription(log)
    };
  },

  // Helper: Generate description from log data
  generateDescription: (log) => {
    const actionConfig = activityLogService.getActionConfig(log.action);
    const entityConfig = activityLogService.getEntityTypeConfig(log.entity_type);
    const userName = log.user ? `${log.user.first_name} ${log.user.last_name}` : 'Someone';
    
    return `${userName} ${actionConfig.verb} ${entityConfig.label.toLowerCase()}`;
  },

  // Helper: Format date
  formatDate: (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  },

  // Helper: Get time ago string
  getTimeAgo: (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  },

  // Helper: Format changes for display
  formatChanges: (changes) => {
    if (!changes || typeof changes !== 'object') {
      return [];
    }

    return Object.entries(changes).map(([field, change]) => {
      if (typeof change === 'object' && change.before !== undefined && change.after !== undefined) {
        return {
          field: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          before: change.before,
          after: change.after
        };
      }
      return null;
    }).filter(Boolean);
  },

  // Helper: Group logs by date
  groupByDate: (logs) => {
    const groups = {};

    logs.forEach(log => {
      const date = new Date(log.created_at);
      const dateKey = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });

    return groups;
  },

  // Helper: Group logs by entity
  groupByEntity: (logs) => {
    const groups = {};

    logs.forEach(log => {
      const key = `${log.entity_type}_${log.entity_id}`;
      if (!groups[key]) {
        groups[key] = {
          entity_type: log.entity_type,
          entity_id: log.entity_id,
          logs: []
        };
      }
      groups[key].logs.push(log);
    });

    return Object.values(groups);
  },

  // Helper: Filter logs
  filterLogs: (logs, filters = {}) => {
    let filtered = [...logs];

    if (filters.action) {
      filtered = filtered.filter(log => log.action === filters.action);
    }

    if (filters.entityType) {
      filtered = filtered.filter(log => log.entity_type === filters.entityType);
    }

    if (filters.userId) {
      filtered = filtered.filter(log => log.user_id === filters.userId);
    }

    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(log => new Date(log.created_at) >= startDate);
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      filtered = filtered.filter(log => new Date(log.created_at) <= endDate);
    }

    return filtered;
  },

  // Helper: Get activity summary
  getActivitySummary: (logs) => {
    const summary = {
      total: logs.length,
      byAction: {},
      byEntityType: {},
      byUser: {},
      recentActivity: logs.slice(0, 5)
    };

    logs.forEach(log => {
      // Count by action
      summary.byAction[log.action] = (summary.byAction[log.action] || 0) + 1;

      // Count by entity type
      summary.byEntityType[log.entity_type] = (summary.byEntityType[log.entity_type] || 0) + 1;

      // Count by user
      if (log.user_id) {
        summary.byUser[log.user_id] = (summary.byUser[log.user_id] || 0) + 1;
      }
    });

    return summary;
  }
};

export default activityLogService;


